import { FocusEvent, memo, useEffect } from 'react';
import { Row } from '@tanstack/react-table';
import { isEqual } from 'lodash';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { NavigableItem } from 'services/schemableFormNavigation/schemableFormNavigation';
import { cn } from 'support/helpers/cn/cn';
import { WEBEDI_CELL_HEIGHT } from 'support/helpers/const/const';

import { AddEmbeddedRowCell } from '../../cells/WebEDI/CreateEdit/AddEmbeddedRowCell';
import { CollapsedEmbeddedGroupCell } from '../../cells/WebEDI/EmbeddedTable/CollapsedEmbeddedGroupCell';
import { RowWithEmbeddedTableRowInfo } from '../../types';
import { Td } from '../Td/Td';
import { calculateCellBasicProperties, calculateCellStyle, planCellRender } from './helpers';

type TableRowProps<TData> = {
  row: Row<TData>;
  rowIndex: number;
  parentRowIndex?: number;
  totalRows: number;
  showCellBorders: boolean;
  columnsWidth: string;
  rowWithEmbeddedTableRowInfo?: RowWithEmbeddedTableRowInfo<TData>;
  /**
   * Not read when rendering. Only `MemoizedTableRow` uses it, as the stand-in for the table's
   * `meta`, which a row reads off the table object at render time — a value a prop comparison
   * cannot see change, because the table object itself is always the same one.
   */
  memoMeta?: unknown;
};

export function TableRow<TData>({
  row,
  rowIndex,
  parentRowIndex,
  totalRows,
  showCellBorders,
  columnsWidth,
  rowWithEmbeddedTableRowInfo,
}: TableRowProps<TData>) {
  const dataPrefix = row.getAllCells()[0].getContext().table.options.meta?.dataPrefix;
  const prefix = dataPrefix ? `${dataPrefix}.` : '';
  const fieldId = `${prefix}${row.id}`;

  // With this we are making the table attached to the error context, not just the cells
  const { service, useNavigableItemPopover, NavigableItemPopoverComponent } = useNavigationContext<NavigableItem>();
  const error = service.useItemByFieldPath(fieldId);
  const setCurrentItem = service.setCurrentItem;
  const hasError = Boolean(error);

  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover({
    placement: 'top-start',
    allowedPlacements: ['top-start', 'bottom-start'],
  });

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      setCurrentItem(error);
    }
  }, [error, setCurrentItem, showNavigableItemPopover]);

  const isEven = (parentRowIndex ?? rowIndex) % 2 === 0;
  const cellClasses = cn([
    hasError ? '' : isEven ? 'bg-white' : 'bg-gray-50', // For row-level errors, don't apply background to cells - let the row handle it
    hasError ? 'text-red-700' : 'text-gray-700',
  ]);

  const visibleCells = row.getVisibleCells();

  // React's onFocus and onBlur bubble. A row with an error must react to focus on any of its cells, so
  // that a click on a cell shows the row's error. Other rows ignore cell focus, because each cell focus
  // would otherwise re-render the whole row.
  const toggleRowPopover = (event: FocusEvent<HTMLTableRowElement>, show: boolean) => {
    if (hasError || event.target === event.currentTarget) {
      setShowNavigableItemPopover(show);
    }
  };

  return (
    <tr
      key={row.id}
      data-field={fieldId}
      tabIndex={error ? 0 : undefined}
      onFocus={(event) => toggleRowPopover(event, true)}
      onBlur={(event) => toggleRowPopover(event, false)}
      className={cn({
        'border border-solid border-red-500!': showNavigableItemPopover && hasError,
        'bg-red-50 hover:bg-red-100': hasError, // Row-level background and hover effect for errors
      })}
      ref={refs.setReference}
      style={{
        height: `${rowWithEmbeddedTableRowInfo?.rowHeight ?? WEBEDI_CELL_HEIGHT}px`,
      }}
    >
      {visibleCells.map((cell, index) => {
        const plan = planCellRender(cell, index, visibleCells, rowWithEmbeddedTableRowInfo);

        if (plan.kind === 'skip') {
          return null;
        }

        const cellProperties = calculateCellBasicProperties(cell, index, visibleCells.length);
        const cellStyle = calculateCellStyle(cellProperties.isAction, columnsWidth, cellProperties.fixedWidth);
        const cellReachesLastRow = rowIndex + (plan.rowSpan ?? 1) - 1 >= totalRows - 1;

        if (plan.kind === 'groupSummary') {
          const cellContext = cell.getContext();

          return (
            <Td
              key={cell.id}
              cell={cell}
              isAction
              colSpan={plan.colSpan}
              rowSpan={plan.rowSpan || 1}
              isLastRow={cellReachesLastRow}
              betweenCellBorders={showCellBorders}
              isFirstColumn={cellProperties.isFirstColumn}
              isLastColumn={index + plan.colSpan >= visibleCells.length}
              hasRowErrorBorder={showNavigableItemPopover && hasError}
              className={cellClasses}
              style={cellStyle}
            >
              {plan.variant === 'collapsed' ? (
                <CollapsedEmbeddedGroupCell
                  row={cellContext.row}
                  table={cellContext.table}
                  itemsCount={plan.itemsCount}
                  parentColumnPath={plan.groupPath}
                />
              ) : (
                <AddEmbeddedRowCell {...cellContext} />
              )}
            </Td>
          );
        }

        return (
          <Td
            key={cell.id}
            isAction={cellProperties.isAction}
            cell={cell}
            isFirstColumn={cellProperties.isFirstColumn}
            isLastColumn={cellProperties.isLastColumn}
            isLastRow={cellReachesLastRow}
            betweenCellBorders={showCellBorders}
            hasRowErrorBorder={showNavigableItemPopover && hasError}
            className={cellClasses}
            style={cellStyle}
            rowSpan={plan.rowSpan}
            rowWithEmbeddedTableRowInfo={rowWithEmbeddedTableRowInfo}
          />
        );
      })}
      {showNavigableItemPopover && hasError && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent annotation={error} viewMode ref={refs.setFloating} style={floatingStyles} />
      )}
    </tr>
  );
}

/**
 * A `TableRow` that skips re-rendering unless something it draws from its props changed. What its
 * cells read from context or the form (`useWatch`, the validation store, locked rows) still updates
 * them on its own. Opt-in (`DataTable`'s `memoizeRows`): only for tables whose cells read their
 * values that way, not from the row data they were rendered with.
 */
export const MemoizedTableRow = memo(TableRow, (previous, next) => rowPropsAreEqual(previous, next)) as typeof TableRow;

const isLastRowOf = <TData,>({ rowIndex, totalRows }: TableRowProps<TData>) => rowIndex >= totalRows - 1;

/** A row whose cells can span rows below it is affected by how many rows follow it. */
const spansRows = <TData,>({ rowWithEmbeddedTableRowInfo }: TableRowProps<TData>) =>
  (rowWithEmbeddedTableRowInfo?.embeddedTableRowsCount ?? 1) > 1;

const rowPropsAreEqual = <TData,>(previous: TableRowProps<TData>, next: TableRowProps<TData>): boolean => {
  const previousInfo = previous.rowWithEmbeddedTableRowInfo;
  const nextInfo = next.rowWithEmbeddedTableRowInfo;

  return (
    previous.row.id === next.row.id &&
    previous.rowIndex === next.rowIndex &&
    previous.parentRowIndex === next.parentRowIndex &&
    previous.showCellBorders === next.showCellBorders &&
    previous.columnsWidth === next.columnsWidth &&
    previous.memoMeta === next.memoMeta &&
    // Only whether it is the last row matters to a row that spans nothing — comparing the count
    // itself would re-render every row each time one is added or removed.
    (spansRows(previous) || spansRows(next)
      ? previous.totalRows === next.totalRows
      : isLastRowOf(previous) === isLastRowOf(next)) &&
    // The row object is new on every change of the table's data, so its content is compared.
    isEqual(previous.row.original, next.row.original) &&
    isEqual(previousInfo, nextInfo && { ...nextInfo, row: previousInfo?.row })
  );
};
